// src/features/monetize/components/OffersTab.tsx

import { useState } from 'react';
import { ActionIcon, Badge, Button, Code, Group, Menu, Paper, Stack, Table, Text } from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { IconArchive, IconDotsVertical, IconPencil, IconPlus } from '@tabler/icons-react';
import type { MonetizeOffer, MonetizeProduct } from '@/lib/types/Monetize';
import { MonetizeUtils } from '../MonetizeUtils';
import { useArchiveOffer } from '../useMonetizeMutations';
import { OfferDrawer } from './OfferDrawer';

interface OffersTabProps {
  offers: MonetizeOffer[];
  products: MonetizeProduct[];
}

export function OffersTab({ offers, products }: OffersTabProps) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState<MonetizeOffer | null>(null);
  const archive = useArchiveOffer();
  const productName = new Map(products.map((p) => [p.id, p.name]));

  function open(offer: MonetizeOffer | null) {
    setEditing(offer);
    setDrawerOpen(true);
  }

  function confirmArchive(offer: MonetizeOffer) {
    modals.openConfirmModal({
      title: `Archive code ${offer.code}?`,
      children: <Text size="sm">The code stops working at checkout. Discounts already applied continue.</Text>,
      labels: { confirm: 'Archive', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        try {
          await archive.mutateAsync(offer.id);
          notifications.show({ title: 'Archived', message: `Code ${offer.code} no longer works.`, color: 'green' });
        } catch (err) {
          notifications.show({ title: 'Error', message: MonetizeUtils.errorMessage(err, 'Failed to archive the code.'), color: 'red' });
        }
      },
    });
  }

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Text size="sm" c="dimmed">Promo codes viewers type at checkout. Partners, launches, win-backs.</Text>
        <Button leftSection={<IconPlus size={16} />} onClick={() => open(null)}>New promo code</Button>
      </Group>
      {offers.length === 0 ? (
        <Paper withBorder p="xl">
          <Stack align="center" gap="xs">
            <Text fw={500}>No promo codes yet</Text>
            <Text size="sm" c="dimmed" ta="center">A code takes a percent or an amount off, for the first payment or for every payment.</Text>
          </Stack>
        </Paper>
      ) : (
        <Paper withBorder style={{ overflow: 'clip' }}>
          <Table highlightOnHover verticalSpacing="sm">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Code</Table.Th>
                <Table.Th>Discount</Table.Th>
                <Table.Th>Applies to</Table.Th>
                <Table.Th>Valid</Table.Th>
                <Table.Th>Used</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th w={48} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {offers.map((o) => (
                <Table.Tr key={o.id} style={o.status === 'archived' ? { opacity: 0.6 } : undefined}>
                  <Table.Td>
                    <Code>{o.code}</Code>
                    <Text size="xs" c="dimmed">{o.name}</Text>
                  </Table.Td>
                  <Table.Td><Text size="sm">{MonetizeUtils.discountLabel(o)}</Text></Table.Td>
                  <Table.Td><Text size="sm">{o.productId ? productName.get(o.productId) ?? 'One product' : 'Any product'}</Text></Table.Td>
                  <Table.Td>
                    <Text size="sm" c="dimmed">
                      {o.startsAt || o.endsAt ? `${o.startsAt ? MonetizeUtils.formatWhen(o.startsAt) : 'Now'} – ${o.endsAt ? MonetizeUtils.formatWhen(o.endsAt) : 'no end'}` : 'Always'}
                    </Text>
                  </Table.Td>
                  <Table.Td><Text size="sm">{o.redemptions}{o.maxRedemptions ? ` / ${o.maxRedemptions}` : ''}</Text></Table.Td>
                  <Table.Td><Badge color={o.status === 'active' ? 'green' : 'gray'} variant="light" size="sm">{o.status === 'active' ? 'Active' : 'Archived'}</Badge></Table.Td>
                  <Table.Td>
                    {o.status === 'active' && (
                      <Menu position="bottom-end" withinPortal>
                        <Menu.Target>
                          <ActionIcon variant="subtle" color="gray" aria-label="Code actions"><IconDotsVertical size={16} /></ActionIcon>
                        </Menu.Target>
                        <Menu.Dropdown>
                          <Menu.Item leftSection={<IconPencil size={14} />} onClick={() => open(o)}>Edit</Menu.Item>
                          <Menu.Item color="red" leftSection={<IconArchive size={14} />} onClick={() => confirmArchive(o)}>Archive</Menu.Item>
                        </Menu.Dropdown>
                      </Menu>
                    )}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Paper>
      )}
      <OfferDrawer opened={drawerOpen} offer={editing} products={products} onClose={() => setDrawerOpen(false)} />
    </Stack>
  );
}
